import { allows } from "@giftforward/entitlements";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { SECONDARY_BUTTON_CLASS } from "@/constants/buttons";
import { type Recipient, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { countryNameFromCode, matchCountryCode } from "@/lib/country";
import {
  normalizeSendDate,
  normalizeSendTime,
  unreadableSendEditLine,
} from "@/utils/sendSchedule-helper";
import { resolveScheduleTimeZone, scheduleZoneCode } from "@/utils/timezone-helper";

/** Compact outline button used in the list header's add-actions (Bulk Upload / Add Manually). */
const ADD_BTN_CLASS = twMerge(
  "flex items-center gap-2 px-3.5 py-2 rounded-btn transition-colors bg-white hover:border-dark-dust hover:bg-clear-dust",
  SECONDARY_BUTTON_CLASS,
);

const PAGE_SIZE = 25;

export type SortField = keyof Omit<Recipient, "id">;
type SortDir = "asc" | "desc";

const COLUMNS: {
  key: SortField;
  label: string;
  /** The campaign_send constraint this column depends on. Absent = always shown. */
  needs?: "regions" | "scheduling";
  get: (r: Recipient) => string | undefined;
}[] = [
  { key: "firstName", label: "First Name", get: (r) => r.firstName },
  { key: "lastName", label: "Last Name", get: (r) => r.lastName },
  { key: "email", label: "Email", get: (r) => r.email },
  { key: "toCompany", label: "Company", get: (r) => r.toCompany },
  {
    key: "recipientCountry",
    label: "Country",
    needs: "regions",
    // Stored as an ISO code; show the country name (fall back to the raw value for an unmapped bulk
    // import the client still has to fix). Search + sort therefore key off the name too.
    get: (r) => countryNameFromCode(r.recipientCountry) ?? r.recipientCountry,
  },
  { key: "senderFirst", label: "Sender First Name", get: (r) => r.senderFirst },
  { key: "senderLast", label: "Sender Last Name", get: (r) => r.senderLast },
  { key: "fromEmail", label: "Sender Email", get: (r) => r.fromEmail },
  { key: "senderSignature", label: "Sender Signature", get: (r) => r.senderSignature },
  { key: "senderCompany", label: "Sender Company", get: (r) => r.senderCompany },
  { key: "sendDate", label: "Send Date", needs: "scheduling", get: (r) => r.sendDate },
  { key: "sendTime", label: "Send Time", needs: "scheduling", get: (r) => r.sendTime },
];

interface Props {
  recipients: Recipient[];
  onDelete: (ids: Set<string>) => void;
  onEdit: (id: string, field: SortField, value: string) => void;
  /** Add actions live here (not above the box) once rows exist — the empty state is gone by then,
   *  so the populated list is the only place left to add more. */
  onBulkUpload: () => void;
  onAddManually: () => void;
}

export function RecipientsTable({
  recipients,
  onDelete,
  onEdit,
  onBulkUpload,
  onAddManually,
}: Props) {
  const { ent } = useMyEntitlements();
  const { notificationMode, scheduleMode, scheduleTz, openModal, closeModal } =
    useCampaignBuilder();
  // Send times are read in the campaign's zone; the header says which, since nothing else on Step 2
  // shows it under "Send on different dates".
  const sendTimeZoneCode = scheduleZoneCode(resolveScheduleTimeZone(scheduleTz));
  const visibleColumns = useMemo(() => {
    if (!ent) return COLUMNS.filter((c) => !c.needs);
    const shows = {
      regions: allows(ent, "campaign_send", "regions", "international"),
      // Per-recipient Send Date/Time are gfg-only: a manual campaign distributes links itself and
      // GfG never sends, so for a "link to a list" (Path B) campaign these columns would be editable
      // noise with no effect. Hidden there, so nothing meaningless is shown, edited, or validated.
      // Same under Send ASAP / Schedule for later: the Step 2 payload drops a row's own date there
      // (GFG-443), so a date shown in the table would be lost on save without a word.
      scheduling:
        allows(ent, "campaign_send", "scheduling", "per_recipient") &&
        notificationMode !== "manual" &&
        scheduleMode === "different",
    };
    return COLUMNS.filter((c) => !c.needs || shows[c.needs]);
  }, [ent, notificationMode, scheduleMode]);

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("firstName");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowId: string; colKey: SortField } | null>(null);
  const [editValue, setEditValue] = useState("");
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Close more menu on outside click
  useEffect(() => {
    if (!moreMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreMenuOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((r) =>
      visibleColumns.some((col) => col.get(r)?.toLowerCase().includes(q)),
    );
  }, [recipients, search, visibleColumns]);

  const sorted = useMemo(() => {
    const col = visibleColumns.find((c) => c.key === sortField);
    if (!col) return [...filtered];
    return [...filtered].sort((a, b) => {
      const av = (col.get(a) ?? "").toLowerCase();
      const bv = (col.get(b) ?? "").toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir, visibleColumns]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageItems = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Keep page in bounds when totalPages shrinks (e.g. after row deletions).
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages - 1));
  }, [totalPages]);

  const allPageSelected = pageItems.length > 0 && pageItems.every((r) => selectedIds.has(r.id));
  const somePageSelected = pageItems.some((r) => selectedIds.has(r.id));
  const selectionCount = selectedIds.size;

  // Drive the indeterminate state on the select-all checkbox via ref
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        for (const r of pageItems) next.delete(r.id);
      } else {
        for (const r of pageItems) next.add(r.id);
      }
      return next;
    });
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleDeleteSelected = () => {
    onDelete(selectedIds);
    setSelectedIds(new Set());
    setMoreMenuOpen(false);
  };

  const handleDeleteOne = (id: string) => {
    onDelete(new Set([id]));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const startEdit = (rowId: string, colKey: SortField, currentValue: string) => {
    setEditingCell({ rowId, colKey });
    setEditValue(currentValue);
  };

  const commitEdit = () => {
    if (editingCell) {
      // Country cells homologate on save (name / alias / code -> ISO code), the same as bulk import;
      // an unrecognised value is kept as typed so the client can still see and fix it.
      const value =
        editingCell.colKey === "recipientCountry"
          ? (matchCountryCode(editValue) ?? editValue.trim())
          : editingCell.colKey === "sendDate"
            ? normalizeSendDate(editValue)
            : editingCell.colKey === "sendTime"
              ? normalizeSendTime(editValue)
              : editValue;
      // A send date/time the API cannot read would save as no date at all (GFG-443): an unreadable
      // edit is discarded, the cell keeps its value and the sender is told; a cleared one clears it.
      if (value !== null) {
        onEdit(editingCell.rowId, editingCell.colKey, value ?? "");
      } else if (editingCell.colKey === "sendDate" || editingCell.colKey === "sendTime") {
        openModal({
          context: "sendDefaultsApplied",
          noticeLines: [unreadableSendEditLine(editingCell.colKey, editValue)],
          onPrimary: closeModal,
        });
      }
      setEditingCell(null);
    }
  };

  const cancelEdit = () => setEditingCell(null);

  const rangeStart = sorted.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, sorted.length);

  return (
    <div>
      {/* ── List header ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4 px-6 py-3.5 bg-clear-dust border-b border-smoke flex-wrap">
        {/* Counter + selection chip */}
        <div className="font-lato text-[13px] text-graphite flex items-center">
          <span>
            Showing{" "}
            <strong className="text-rodeo-black font-bold">
              {filtered.length} / {recipients.length}
            </strong>{" "}
            Recipients
          </span>
          {selectionCount > 0 && (
            <span className="ml-2.5 pl-2.5 border-l border-smoke text-rodeo-black font-semibold flex items-center gap-1">
              <strong className="text-dark-dust font-bold">{selectionCount}</strong> selected
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="bg-transparent border-none text-dark-dust cursor-pointer font-lato text-[13px] underline pl-1 hover:text-rodeo-black"
              >
                Clear
              </button>
            </span>
          )}
        </div>

        {/* Controls: add actions + search + more menu */}
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onBulkUpload} className={ADD_BTN_CLASS}>
            <GroupAddIcon sx={{ fontSize: 18 }} />
            Bulk Upload
          </button>
          <button type="button" onClick={onAddManually} className={ADD_BTN_CLASS}>
            <PersonAddAltIcon sx={{ fontSize: 18 }} />
            Add Manually
          </button>
          <div className="relative flex items-center">
            <input
              type="text"
              placeholder="Find in list..."
              aria-label="Search recipients"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-55 pl-3 pr-9 py-2 border border-smoke rounded-btn bg-white font-lato text-[13px] text-rodeo-black placeholder:text-graphite focus:outline-none focus:border-dark-dust transition-colors"
            />
            <SearchIcon
              sx={{ fontSize: 18 }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-graphite pointer-events-none"
            />
          </div>

          <div className="relative inline-flex" ref={moreMenuRef}>
            <button
              type="button"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={moreMenuOpen}
              onClick={() => setMoreMenuOpen((v) => !v)}
              className={`bg-white border border-smoke rounded-btn w-9 h-9 inline-flex items-center justify-center p-0 transition-colors hover:bg-clear-dust hover:border-dark-dust hover:text-rodeo-black ${moreMenuOpen ? "bg-clear-dust border-dark-dust text-rodeo-black" : "text-graphite"}`}
            >
              <MoreVertIcon sx={{ fontSize: 20 }} />
            </button>

            {moreMenuOpen && (
              <div
                role="menu"
                className="absolute top-[calc(100%+6px)] right-0 min-w-55 bg-white border border-smoke rounded-sm shadow-[0_8px_24px_rgba(0,0,0,0.10)] py-1.5 z-50"
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={selectionCount === 0}
                  onClick={handleDeleteSelected}
                  className={`flex items-center gap-2.5 px-3.5 py-2.25 w-full bg-transparent border-none font-lato text-[13px] text-left transition-colors ${selectionCount > 0 ? "text-good-wine cursor-pointer hover:bg-good-wine-bg" : "text-smoke cursor-not-allowed"}`}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  Delete selected ({selectionCount})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="w-8 pl-3 pr-1 text-center bg-white py-3.5 border-b border-smoke">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all recipients"
                  className="w-4 h-4 cursor-pointer align-middle gfg-checkbox"
                />
              </th>
              <th className="w-12 bg-white font-lato text-[12px] font-semibold text-graphite py-3.5 px-3 border-b border-smoke text-left">
                #
              </th>
              {visibleColumns.map((col) => {
                const active = sortField === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    className="bg-white font-lato text-[12px] font-semibold text-rodeo-black py-3.5 px-3 border-b border-smoke text-left cursor-pointer select-none whitespace-nowrap group/th"
                  >
                    <span className="mr-1.5">
                      {col.key === "sendTime" ? `${col.label} (${sendTimeZoneCode})` : col.label}
                    </span>
                    {active ? (
                      sortDir === "asc" ? (
                        <ArrowDownwardIcon
                          sx={{
                            fontSize: 14,
                            verticalAlign: "middle",
                            color: "var(--color-rodeo-black)",
                          }}
                        />
                      ) : (
                        <ArrowUpwardIcon
                          sx={{
                            fontSize: 14,
                            verticalAlign: "middle",
                            color: "var(--color-rodeo-black)",
                          }}
                        />
                      )
                    ) : (
                      <UnfoldMoreIcon
                        sx={{
                          fontSize: 14,
                          verticalAlign: "middle",
                          color: "var(--color-graphite)",
                          opacity: 0.55,
                        }}
                        className="transition-opacity group-hover/th:opacity-100"
                      />
                    )}
                  </th>
                );
              })}
              <th className="w-14 bg-white py-3.5 px-3 border-b border-smoke" />
            </tr>
          </thead>
          <tbody>
            {pageItems.map((r, i) => {
              const selected = selectedIds.has(r.id);
              return (
                <tr
                  key={r.id}
                  className={`group/row transition-colors ${selected ? "bg-clear-dust" : "hover:bg-clear-dust"}`}
                >
                  <td className="w-8 pl-3 pr-1 text-center py-3 border-b border-smoke">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleRow(r.id)}
                      aria-label="Select recipient"
                      className="w-4 h-4 cursor-pointer align-middle gfg-checkbox"
                    />
                  </td>
                  <td className="w-12 py-3 px-3 border-b border-smoke font-lato text-[13px] text-graphite">
                    {page * PAGE_SIZE + i + 1}
                  </td>
                  {visibleColumns.map(({ key, get }) => {
                    const isEditing = editingCell?.rowId === r.id && editingCell?.colKey === key;
                    const cellValue = get(r) ?? "";
                    return (
                      <td
                        key={key}
                        className="py-0 px-0 border-b border-smoke font-lato text-[13px] text-rodeo-black"
                        style={
                          isEditing
                            ? { outline: "2px solid var(--color-dark-dust)", outlineOffset: "-2px" }
                            : undefined
                        }
                      >
                        {isEditing ? (
                          <input
                            // biome-ignore lint/a11y/noAutofocus: intentional cell editor
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                commitEdit();
                              }
                              if (e.key === "Escape") cancelEdit();
                            }}
                            className="w-full py-3 px-3 bg-transparent outline-none font-lato text-[13px] text-rodeo-black"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEdit(r.id, key, cellValue)}
                            className="w-full text-left py-3 px-3 bg-transparent border-none cursor-text font-lato text-[13px] text-rodeo-black"
                          >
                            {cellValue || <span className="text-graphite italic">—</span>}
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="w-14 py-3 px-3 border-b border-smoke text-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteOne(r.id)}
                      aria-label="Remove recipient"
                      className="inline-flex items-center justify-center w-7 h-7 rounded bg-transparent border-none text-graphite cursor-pointer opacity-0 group-hover/row:opacity-100 hover:text-good-wine hover:bg-good-wine-bg transition-all"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Footer / Pagination ───────────────────────────────────── */}
      <div className="flex items-center justify-end px-6 py-3.5 font-lato text-[13px] text-graphite border-t border-smoke">
        <div className="flex items-center gap-1">
          <span className="mr-2">
            {rangeStart}–{rangeEnd} of {sorted.length}
          </span>
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            aria-label="Previous page"
            className="w-7 h-7 rounded bg-transparent border-none cursor-pointer inline-flex items-center justify-center transition-colors text-graphite enabled:hover:bg-clear-dust enabled:hover:text-rodeo-black disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
            className="w-7 h-7 rounded bg-transparent border-none cursor-pointer inline-flex items-center justify-center transition-colors text-graphite enabled:hover:bg-clear-dust enabled:hover:text-rodeo-black disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );
}
